
import { useState, useTransition, useEffect } from 'react';
import { format } from 'date-fns';
import { getWeatherAction } from '@/app/actions';
import type { Loch } from '@/app/lib/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Loader2, Sparkles, Sun, Droplets, Wind, Telescope, Thermometer } from 'lucide-react';
import { FavoriteButton } from './FavoriteButton';
import { LogSwimButton } from './LogSwimButton';
import { Weather } from './Weather';

type LocationDetailsProps = {
  location: Loch;
};

type WeatherState = {
  data?: any;
  error?: string;
};

export function LocationDetails({ location }: LocationDetailsProps) {
  const [isWeatherPending, startWeatherTransition] = useTransition();


  const [weatherData, setWeatherData] = useState<WeatherState | null>(null);


  const handleFetchWeather = () => {
    startWeatherTransition(async () => {
      const result = await getWeatherAction(location);
      if (result.success) {
        setWeatherData({ data: result.data });
      } else {
        setWeatherData({ error: result.error });
      }
    });
  };

  useEffect(() => {
    handleFetchWeather();
  }, []);



  const todayForecast = weatherData?.data?.forecast?.[0];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <FavoriteButton locationId={location.id} />
        <LogSwimButton location={location} />
      </div>
      <Separator />
      {!weatherData && (
        <Card>
          <CardHeader>
            <CardTitle>Weather Forecast</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center text-center gap-4 py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="text-muted-foreground">Loading forecast...</p>
            </div>
          </CardContent>
        </Card>
      )}
      {weatherData?.error && (
        <Card>
          <CardHeader>
            <CardTitle>Weather Forecast</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-destructive text-center">{weatherData.error}</p>
          </CardContent>
        </Card>
      )}
      {weatherData?.data && <Weather data={weatherData.data} />}
    </div>
  );
}
